import _keyBy from 'lodash/keyBy';
import { useCallback, useMemo } from 'react';
import {
  MemberType,
  ProjectId,
  Role,
  Team,
  User,
  UserStatus,
  UserType,
} from 'api/models';
import { PartnerPermissionName } from 'api/partner/types';
import {
  AssigneeValue,
  TeamId,
  UserId,
} from 'components/organisms/tasks/fields';
import { useProjectDetail } from 'data/projects/hooks';
import { useAllCompanies } from 'queries/account';
import { useHideNonStrategicPartner } from 'queries/partner';
import {
  ResolvedWithCompany,
  usePlaceholderResolutionByProjectQueryV2,
} from 'queries/placeholder';
import { useRolesWithPlaceholderQuery } from 'queries/roles';
import { byLabel } from 'utils/comparators/by-label';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';
import { usePlaceholderValueHelpers } from 'utils/placeholders';
import { OptionGroup } from '../components/organisms/field-editors/components/controlled-select-based-editor-popover';
import {
  useGetAllUsers,
  useGetAllMembersInProject,
  useGetAllMembersNotInProject,
  useGetAllTeamsFromIndexedDb,
  useGetAllMembersByCompany,
} from './use-idb';
import { useIsRestrictedPartnerUser } from './use-partner';
import { useTranslate } from './use-translation';
import { useUser } from './use-user';

export enum AssigneePickerFilter {
  All = 'ALL',
  ActiveUsers = 'ACTIVE_USERS',
  InProjectUsers = 'IN_PROJECT_USERS',
  ExternalUsers = 'EXTERNAL_USERS',
  NativeUsers = 'NATIVE_USERS',
  NativeUsersAndTeams = 'NATIVE_USERS_AND_TEAMS',
  NativePartnerUsersAndTeams = 'NATIVE_PARTNER_USERS_AND_TEAMS',
  InProjectUsersAndTeams = 'IN_PROJECT_USERS_AND_TEAMS',
}
interface ProjectUsersOptions {
  includeRestricedPartnersMembers?: boolean;
}

type UseAssigneeOptions = {
  projectId?: ProjectId;
  isPrivate?: boolean;
  filter: AssigneePickerFilter;
  hidePlaceholders?: boolean;
  options?: ProjectUsersOptions;
  includeDeactivatedUsers?: boolean;
};

interface GenerateAssigneePickerOptionsResult {
  generateAssigneePickerOptions: (values: {
    users: User[];
    teams: Team[];
    usersNotInProject: User[];
    roles: Role[];
  }) => OptionGroup<string>[];
}

export const useGenerateAssigneePickerOptions = (
  includeDeactivatedUsers = false,
): GenerateAssigneePickerOptionsResult => {
  const { getResponsibleCompany } = usePlaceholderValueHelpers();
  const translate = useTranslate();
  const getRoleMap = useCallback(
    (roles: Role[]) =>
      roles
        ? roles.reduce((previous, role) => {
            previous[role.roleId] = role;

            return previous;
          }, {})
        : undefined,
    [],
  );

  const generateAssigneePicker = useCallback(
    ({
      users,
      teams,
      usersNotInProject,
      roles,
    }: {
      users: User[];
      teams: Team[];
      usersNotInProject: User[];
      roles: Role[];
    }): OptionGroup<string>[] => {
      const roleMap: { [id: number]: Role } | undefined = getRoleMap(roles);

      const assigneePickerOptions = [
        {
          label: 'Assignees',
          id: 'assignees',
          options: [], // selected, @TODO handle option select
        },
        {
          id: 'users',
          label: translate('user_in_project'),
          options: users?.length
            ? users
                .filter((u) =>
                  includeDeactivatedUsers
                    ? true
                    : u.userStatus !== UserStatus.Inactive,
                )
                .map((u) => ({
                  value: AssigneeValue.toString(UserId(u?.userId)),
                  label: fullName(u),
                  imageUrl: u?.photoUrl,
                  description: u?.role?.roleName,
                  color: getRandomColor(u?.userId),
                  roleId: u?.role?.roleId,
                  userType: u.userType,
                  logoUrl: u?.company.companyLogoUrl,
                  userStatus: u.userStatus,
                  extra: {
                    id: u?.userId,
                    type: 'user',
                    timeOffDetails: u?.userTimeOffDetails,
                    holidayCalendarId: u?.holidayCalendarId,
                  },
                }))
                .sort(byLabel)
            : [],
        },
      ];

      if (teams.length) {
        assigneePickerOptions?.push({
          id: 'teams',
          label: translate('placeholders'),
          options: teams
            .map((t) => ({
              value: AssigneeValue.toString(TeamId(t.teamId)),
              label: t.teamName,
              imageUrl: undefined,
              description: t.roleId
                ? roleMap?.[t.roleId]?.roleName ?? t.teamDescription
                : undefined,
              color: getRandomColor(t.teamId),
              roleId: t.roleId,
              logoUrl: getResponsibleCompany(t)?.companyLogoUrl,
              userType: t.teamType as unknown as UserType,
              userStatus: UserStatus.Active,
              extra: {
                id: t.teamId,
                type: 'team',
                teamType: t.teamType,
                timeOffDetails: undefined,
                holidayCalendarId: undefined,
              },
            }))
            .sort(byLabel),
        });
      }

      if (usersNotInProject?.length) {
        assigneePickerOptions.push({
          id: 'users_not_in_project',
          label: translate('not_in_project'),
          options: usersNotInProject
            .filter((u) =>
              includeDeactivatedUsers
                ? true
                : u.userStatus !== UserStatus.Inactive,
            )
            .map((u) => ({
              value: AssigneeValue.toString(UserId(u.userId)),
              label: fullName(u),
              imageUrl: u.photoUrl,
              description: u.role?.roleName,
              color: getRandomColor(u.userId),
              roleId: u.role?.roleId,
              userType: u.userType,
              logoUrl: u?.company.companyLogoUrl,
              userStatus: u.userStatus,
              extra: {
                id: u.userId,
                type: 'user',
                timeOffDetails: u?.userTimeOffDetails,
                holidayCalendarId: u?.holidayCalendarId,
              },
            }))
            .sort(byLabel),
        });
      }

      return assigneePickerOptions;
    },
    [getResponsibleCompany, getRoleMap, includeDeactivatedUsers, translate],
  );

  return {
    generateAssigneePickerOptions: generateAssigneePicker,
  };
};
interface AssignableTeamsProps {
  projectId?: ProjectId;
  isPrivate?: boolean;
  onlyProjectSpecificPlaceholder?: boolean;
}
export const useGetAssignableTeamsForProject = ({
  projectId,
  isPrivate = false,
  onlyProjectSpecificPlaceholder = false,
}: AssignableTeamsProps): Team[] => {
  const teams = useGetAllTeamsFromIndexedDb();
  const { isCurrentUserCustomer, updateCompanyIfNonStrategic } =
    useHideNonStrategicPartner();
  const { data: companies } = useAllCompanies();
  const isRestrictedPartnerUser = useIsRestrictedPartnerUser();
  const {
    user: {
      company: { companyId: currentUserCompanyId },
    },
  } = useUser();
  const { data: placeholderResolutions } =
    usePlaceholderResolutionByProjectQueryV2(projectId ?? ProjectId(-1), {
      enabled: projectId != null && projectId[1] != null,
    });
  const { getResponsibleCompany } = usePlaceholderValueHelpers();
  const updateCompanyIdIfNonStrategic = useCallback(
    (company: ResolvedWithCompany): ResolvedWithCompany => {
      if (!isCurrentUserCustomer()) {
        return company;
      }
      const companyFromQuery = companies?.find(
        ({ companyId }) => companyId === company.companyId,
      );

      if (companyFromQuery != null) {
        return {
          identityType: 'COMPANY',
          companyId: updateCompanyIfNonStrategic(companyFromQuery).companyId,
        };
      }

      return company;
    },
    [companies, isCurrentUserCustomer, updateCompanyIfNonStrategic],
  );

  return useMemo(() => {
    return teams
      .map((team) => {
        const resolvedWith = placeholderResolutions?.mappings[team.teamId];

        return {
          ...team,
          userList:
            resolvedWith?.identityType === 'USER' ? [resolvedWith] : undefined,
          companyList:
            resolvedWith?.identityType === 'COMPANY'
              ? [updateCompanyIdIfNonStrategic(resolvedWith)]
              : undefined,
          resolved: resolvedWith?.identityType === 'USER',
        };
      })
      .filter((team) => {
        const responsibleCompany = getResponsibleCompany(team);

        if (
          isPrivate &&
          (team.teamType === MemberType.External ||
            (responsibleCompany?.accessLevel?.partnerPermissionSetName !=
              null &&
              [
                PartnerPermissionName.Moderate,
                PartnerPermissionName.Restricted,
              ].includes(
                responsibleCompany?.accessLevel.partnerPermissionSetName,
              )))
        ) {
          return false;
        }
        if (
          isRestrictedPartnerUser &&
          responsibleCompany?.companyId !== currentUserCompanyId
        ) {
          return false;
        }
        if (team.accountLevelTeam && !onlyProjectSpecificPlaceholder) {
          return true;
        }

        return projectId != null
          ? team.projectIds?.includes(projectId[1])
          : false;
      });
  }, [
    currentUserCompanyId,
    getResponsibleCompany,
    isPrivate,
    isRestrictedPartnerUser,
    onlyProjectSpecificPlaceholder,
    placeholderResolutions?.mappings,
    projectId,
    teams,
    updateCompanyIdIfNonStrategic,
  ]);
};

export function useAssigneeOptions({
  projectId,
  isPrivate = false,
  filter,
  hidePlaceholders = false,
  options,
  includeDeactivatedUsers,
}: UseAssigneeOptions): OptionGroup<string>[] {
  const { data: roles } = useRolesWithPlaceholderQuery();
  const { data: projectDetail } = useProjectDetail(
    projectId as unknown as ProjectId,
  );

  const currentUser = useUser();

  const { companyId } = currentUser.company;
  const customerCompanyId = projectDetail?.customer?.companyId;

  const activeMembers = useGetAllUsers({
    includeDeactivatedUsers: false,
  });
  const { data: usersInProject } = useGetAllMembersInProject({
    projectId: projectId?.[1],
    isPrivate,
    options,
  });

  // @TODO handle customerCompanyId missing case in a better way
  const { data: usersNotInProject } = useGetAllMembersNotInProject({
    projectId: projectId?.[1],
    isPrivate,
    options,
  });

  const vendorUserList = useGetAllMembersByCompany(companyId);
  const partnerUsers = useMemo(
    () => activeMembers.filter(({ userType }) => userType === UserType.Partner),
    [activeMembers],
  );
  // @TODO handle customerCompanyId missing case in a better way
  const customerUserList = useGetAllMembersByCompany(customerCompanyId!);
  const teams = useGetAssignableTeamsForProject({
    projectId,
    isPrivate,
  });
  const { generateAssigneePickerOptions } = useGenerateAssigneePickerOptions(
    includeDeactivatedUsers,
  );

  // eslint-disable-next-line no-console
  // console.log(new Error()?.stack?.split('\n')[2].trim().split(' ')[1]);
  return useMemo(() => {
    if (filter === AssigneePickerFilter.All) {
      return generateAssigneePickerOptions({
        users: usersInProject as User[],
        teams: Array.isArray(teams) && !hidePlaceholders ? teams : [],
        usersNotInProject: usersNotInProject as User[],
        roles: roles as Role[],
      });
    }

    if (filter === AssigneePickerFilter.ActiveUsers) {
      return generateAssigneePickerOptions({
        users: activeMembers || [],
        usersNotInProject: [],
        teams: [],
        roles: roles as Role[],
      });
    }

    if (filter === AssigneePickerFilter.NativeUsers) {
      return generateAssigneePickerOptions({
        users: vendorUserList || [],
        usersNotInProject: [],
        teams: [],
        roles: roles as Role[],
      });
    }

    if (filter === AssigneePickerFilter.NativeUsersAndTeams) {
      return generateAssigneePickerOptions({
        users: vendorUserList || [],
        usersNotInProject: [],
        teams: Array.isArray(teams) ? teams : [],
        roles: roles as Role[],
      });
    }
    if (filter === AssigneePickerFilter.NativePartnerUsersAndTeams) {
      return generateAssigneePickerOptions({
        users: (vendorUserList || []).concat(partnerUsers ?? []),
        usersNotInProject: [],
        teams: Array.isArray(teams) ? teams : [],
        roles: roles as Role[],
      });
    }

    if (filter === AssigneePickerFilter.ExternalUsers) {
      return generateAssigneePickerOptions({
        users: customerUserList || [],
        usersNotInProject: [],
        teams: [],
        roles: roles as Role[],
      });
    }

    if (filter === AssigneePickerFilter.InProjectUsers) {
      return generateAssigneePickerOptions({
        users: usersInProject || [],
        usersNotInProject: [],
        teams: [],
        roles: roles as Role[],
      });
    }

    if (filter === AssigneePickerFilter.InProjectUsersAndTeams) {
      return generateAssigneePickerOptions({
        users: usersInProject || [],
        usersNotInProject: [],
        teams: Array.isArray(teams) ? teams : [],
        roles: roles as Role[],
      });
    }

    return [];
  }, [
    activeMembers,
    customerUserList,
    filter,
    generateAssigneePickerOptions,
    hidePlaceholders,
    partnerUsers,
    roles,
    teams,
    usersInProject,
    usersNotInProject,
    vendorUserList,
  ]);
}

interface ProjectUsersType {
  inProjectUsers: User[];
  notInProjectUsers: User[];
}

interface ProjectUsersProps {
  projectId: ProjectId;
  isPrivate?: boolean;
  options?: ProjectUsersOptions;
  includeDeactivatedUsers?: boolean;
}

export const useInAndNotInProjectUsers = ({
  projectId,
  isPrivate = false,
  options,
  includeDeactivatedUsers,
}: ProjectUsersProps): ProjectUsersType => {
  const { data: dataInProjectUsers } = useGetAllMembersInProject({
    projectId: projectId[1],
    isPrivate,
    options,
  });

  const inProjectUsers = dataInProjectUsers.filter((u) =>
    includeDeactivatedUsers ? true : u.userStatus !== UserStatus.Inactive,
  );

  const { data: dataNotInProjectUsers } = useGetAllMembersNotInProject({
    projectId: projectId[1],
    isPrivate,
    options,
  });

  const notInProjectUsers = dataNotInProjectUsers.filter((u) =>
    includeDeactivatedUsers ? true : u.userStatus !== UserStatus.Inactive,
  );

  return useMemo(
    () => ({
      inProjectUsers,
      notInProjectUsers,
    }),
    [inProjectUsers, notInProjectUsers],
  );
};
